import { createAsyncThunk } from '@reduxjs/toolkit'
import { ServiceConfig } from '../services/config'
import {
  APIError,
  get,
  isAPIValidationError,
  post,
  put,
  remove,
} from '../services/crud'
import { StoreData } from '../reducers/typings'
import {
  Territory,
  TerritoryBoundary,
  TerritoryPerformance,
  TerritoryPerformanceReport,
  TerritoryRuleUsage,
} from './typings'

/** Every territory path comes from ServiceConfig; none is spelled out here. */
const territoryUrl = (path: string = ServiceConfig.territory.territory) =>
  `${ServiceConfig.baseAPIUrl}${path}`

/**
 * Every territory in the tenant WITHOUT geometry (WWM-17508).
 *
 * Fetched once when the map mounts. The response carries each territory's `bounds`, which is what
 * lets the client decide which polygons the viewport touches without another server call. Measured
 * at ~255 bytes per territory, against ~5.6 KB gzipped for one with geometry.
 */
export const fetchTerritorySummaries = createAsyncThunk<
  Territory[],
  void,
  { state: StoreData; rejectValue: APIError }
>('territory/fetchSummaries', async (_, thunkApi) => {
  const response = await get(`${territoryUrl()}?includeBoundary=false`)

  if (response.ok) return (await response.json()) as Territory[]

  return thunkApi.rejectWithValue((await response.json()) as APIError)
})

/**
 * Must match TerritoryController.MaxIdsPerRequest. The server rejects a longer list with a 400,
 * and because a rejected fetch releases its ids the same oversized request would be retried on
 * every pan -- so a tenant with many territories would simply never see polygons.
 */
export const MAX_IDS_PER_REQUEST = 200

function chunk(ids: number[], size: number): number[][] {
  const chunks: number[][] = []
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size))
  return chunks
}

/**
 * Geometry for a specific set of territories — the cache fill for whatever the viewport touches.
 *
 * Ids the server no longer recognises are simply absent from the response (a territory deleted
 * between the summary fetch and this one is a normal race), so the reducer must not assume it gets
 * back everything it asked for.
 */
export const fetchTerritoryBoundaries = createAsyncThunk<
  Territory[],
  number[],
  { state: StoreData; rejectValue: APIError }
>('territory/fetchBoundaries', async (ids, thunkApi) => {
  if (!ids.length) return []

  const batches = chunk(ids, MAX_IDS_PER_REQUEST)
  const results: Territory[] = []

  for (const batch of batches) {
    const response = await get(`${territoryUrl()}?ids=${batch.join(',')}`)

    if (!response.ok) {
      // Keep whatever earlier batches returned. Discarding them would throw away geometry the
      // server already sent and paid for, and the caller handles a short response natively --
      // the reducer caches what it gets and releases every requested id, so the ids this failed
      // batch covers are simply refetched the next time the viewport touches them.
      if (results.length) return results

      return thunkApi.rejectWithValue((await response.json()) as APIError)
    }

    results.push(...((await response.json()) as Territory[]))
  }

  return results
})

/**
 * A write failure, already reduced to something worth showing a user.
 *
 * The territory write endpoints answer in three different shapes and the dialog should not have
 * to know that:
 *   - FluentValidation rejects a bad shape with { errors: { Boundary: [...] }, title },
 *   - BadRequestException (duplicate name, unknown sales team) with { StatusCode, Message },
 *   - the Admin role gate with 403 and an EMPTY body, which response.json() throws on.
 */
export type TerritoryWriteError = {
  message: string
  statusCode: number
}

/**
 * Reduce any of those to one readable sentence.
 *
 * The server's own wording is preferred wherever there is some -- "Boundary is not a valid
 * polygon: Self-intersection." tells the user what to do differently, where a generic failure
 * does not. Only the cases that genuinely carry no message get a canned one.
 */
type TerritoryErrorFallbacks = {
  /** A 403 with no body, which is what the Admin gate returns. */
  forbidden: string
  /** Anything else the server did not explain. */
  generic: string
}

/**
 * Wording for a failed WRITE. These are only ever reached when the server said nothing useful.
 */
const WRITE_FALLBACKS: TerritoryErrorFallbacks = {
  forbidden: 'Only an administrator can create or change territories.',
  generic: 'Could not save this territory. Please try again.',
}

/**
 * Wording for a failed READ.
 *
 * The read thunks used the write wording, so a body-less 403 on /performance told the user
 * "Only an administrator can create or change territories." when they had not tried to change
 * anything, and an empty 5xx on the summary bubble said "Could not save this territory."
 */
const READ_FALLBACKS: TerritoryErrorFallbacks = {
  forbidden: 'You do not have permission to view this territory information.',
  generic: 'Could not load this territory information. Please try again.',
}

async function toTerritoryError(
  response: Awaited<ReturnType<typeof post>>,
  fallbacks: TerritoryErrorFallbacks = WRITE_FALLBACKS,
): Promise<TerritoryWriteError> {
  // Deliberately NOT crud's isUnexpectedResponseError. That guard is `ok === false && json
  // !== undefined`, and a genuine failed Response satisfies both -- so using it here reported
  // every 400 and 403 as "could not reach the server", hiding the real reason. Presence of
  // text() is what actually separates a real Response from crud's fabricated object.
  if (typeof (response as Partial<Response>).text !== 'function') {
    return {
      statusCode: response.status,
      message:
        'Could not reach the server. Check your connection and try again.',
    }
  }

  const httpResponse = response as Response
  const statusCode = httpResponse.status

  // 403 from the Admin gate has no body at all, and .json() on it throws.
  const raw = await httpResponse.text()
  if (!raw) {
    return {
      statusCode,
      message: statusCode === 403 ? fallbacks.forbidden : fallbacks.generic,
    }
  }

  let body: unknown
  try {
    body = JSON.parse(raw)
  } catch {
    return {
      statusCode,
      message: fallbacks.generic,
    }
  }

  if (isAPIValidationError(body)) {
    // Field errors read well on their own ("Boundary is not a valid polygon: ..."), so they are
    // joined rather than prefixed with field names the user never saw.
    const messages = Object.values(body.errors ?? {}).flat()
    if (messages.length) return { statusCode, message: messages.join(' ') }
  }

  // ProblemDetails, which the 404 uses. It also has a `title`, so the validation guard above
  // matches it first and finds no `errors` -- its message lives in `detail`.
  const problem = body as { detail?: string }
  if (problem?.detail) return { statusCode, message: problem.detail }

  const apiError = body as Partial<APIError>
  if (apiError?.Message) return { statusCode, message: apiError.Message }

  return {
    statusCode,
    message: 'Could not save this territory. Please try again.',
  }
}

/** What the create form collects. Mirrors TerritoryInput on the server. */
export type CreateTerritoryPayload = {
  name: string
  salesTeamId: number
  ownerIds: string[]
  colorHex: string
  boundary: TerritoryBoundary
}

/**
 * Create a territory the admin just drew (WWM-17508).
 *
 * Admin-only: TerritoryController gates POST with [Authorize(Roles = "Admin")], so the button
 * that reaches this is gated on currentUser.isAdmin to match. A non-admin who got here anyway
 * gets a 403, which surfaces as the message below rather than a silent failure.
 *
 * The server is the authority on whether the SHAPE is acceptable -- it rejects self-intersection,
 * out-of-range coordinates and absurd vertex counts. Its message is passed through verbatim so
 * the dialog can show the real reason ("the boundary crosses itself") instead of a generic
 * failure, which is the whole point of not duplicating that validation on the client.
 */
export const createTerritory = createAsyncThunk<
  Territory,
  CreateTerritoryPayload,
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/create', async (payload, thunkApi) => {
  const response = await post(territoryUrl(), payload)

  if (response.ok) return (await response.json()) as Territory

  return thunkApi.rejectWithValue(await toTerritoryError(response))
})

/** A territory id plus everything the server needs to replace it. Mirrors TerritoryInput. */
export type UpdateTerritoryPayload = CreateTerritoryPayload & { id: number }

/**
 * Replace a territory (WWM-17508, Feature A).
 *
 * PUT is a full replace -- TerritoryInputValidator requires Boundary even when only the name
 * changed -- so the caller must have the geometry in hand. The summary feed deliberately omits
 * it, which is why editing details waits for the boundary cache to fill first.
 */
export const updateTerritory = createAsyncThunk<
  Territory,
  UpdateTerritoryPayload,
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/update', async ({ id, ...body }, thunkApi) => {
  const response = await put(
    territoryUrl(ServiceConfig.territory.byId(id)),
    body,
  )

  if (response.ok) return (await response.json()) as Territory

  return thunkApi.rejectWithValue(await toTerritoryError(response))
})

/**
 * Soft-delete a territory.
 *
 * Resolves to the id rather than a body: the endpoint answers 204 with nothing, and the id is
 * what the reducer needs to drop it from the summaries and the geometry cache.
 */
export const deleteTerritory = createAsyncThunk<
  number,
  number,
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/delete', async (id, thunkApi) => {
  const response = await remove(territoryUrl(ServiceConfig.territory.byId(id)))

  if (response.ok) return id

  return thunkApi.rejectWithValue(await toTerritoryError(response))
})

/**
 * Which assignment rules name this territory in a condition (WWM-17508).
 *
 * Read when the delete dialog opens, not kept in the slice: it is a question about OTHER
 * entities, asked once at the moment it matters, and caching it would risk confirming a delete
 * against a rule list that has since changed.
 */
export const fetchTerritoryRuleUsage = createAsyncThunk<
  TerritoryRuleUsage[],
  number,
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/ruleUsage', async (id, thunkApi) => {
  const response = await get(
    territoryUrl(ServiceConfig.territory.ruleUsage(id)),
  )

  if (response.ok) return (await response.json()) as TerritoryRuleUsage[]

  return thunkApi.rejectWithValue(
    await toTerritoryError(response, READ_FALLBACKS),
  )
})

/**
 * Leads, visits and closes by territory for a date range (WWM-17508).
 *
 * Not admin-gated, unlike the write endpoints: reviewing performance is a sales manager's job,
 * and every number comes from records the caller can already see.
 */
export const fetchTerritoryPerformance = createAsyncThunk<
  TerritoryPerformanceReport,
  { from: string; to: string },
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/performance', async ({ from, to }, thunkApi) => {
  const response = await get(
    `${territoryUrl(ServiceConfig.territory.performance)}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  )

  if (response.ok) return (await response.json()) as TerritoryPerformanceReport

  return thunkApi.rejectWithValue(
    await toTerritoryError(response, READ_FALLBACKS),
  )
})

/**
 * How many days of activity a boundary preview covers.
 *
 * Twelve months, not the report's rolling thirty days: drawing a territory is a planning
 * decision, and a month is too short a window to judge an area by. Contacts and size do not
 * depend on it at all, which is why they are the numbers to trust when the activity is thin.
 */
export const PREVIEW_WINDOW_DAYS = 365

/**
 * What a candidate boundary WOULD contain, without saving it (WWM-17508).
 *
 * Nothing is written. This only works because territory membership is never stored, so a
 * hypothetical shape costs the same as a real one -- which is what makes it affordable to ask
 * the question on every stroke.
 */
export const fetchTerritoryPreview = createAsyncThunk<
  TerritoryPerformance,
  TerritoryBoundary,
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/preview', async (boundary, thunkApi) => {
  const response = await requestPreview(boundary)

  if (response.ok) return (await response.json()) as TerritoryPerformance

  return thunkApi.rejectWithValue(
    await toTerritoryError(response, READ_FALLBACKS),
  )
})

/** The one request both the draft preview and the saved-territory snapshot make. */
function requestPreview(boundary: TerritoryBoundary) {
  const to = new Date()
  const from = new Date(
    to.getTime() - PREVIEW_WINDOW_DAYS * 24 * 60 * 60 * 1000,
  )

  return post(
    `${territoryUrl(ServiceConfig.territory.preview)}?from=${from.toISOString()}&to=${to.toISOString()}`,
    boundary,
  )
}

/**
 * What one SAVED territory currently contains, for the summary bubble (WWM-17508).
 *
 * The same `/preview` request the draft panel makes, posted with the territory's stored
 * boundary. Deliberately not a second endpoint: a saved territory and a drawn shape are the same
 * question asked of the same geometry, and membership is computed live either way, so a
 * `GET /Territory/{id}/performance` would be a parallel code path returning identical numbers --
 * and a chance for the bubble and the panel to disagree.
 *
 * The id is carried for the cache alone; the server never sees it. It is required rather than
 * derived, because the caller already knows which polygon it took the boundary from.
 */
export const fetchTerritorySnapshot = createAsyncThunk<
  TerritoryPerformance,
  { id: number; boundary: TerritoryBoundary },
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/snapshot', async ({ boundary }, thunkApi) => {
  const response = await requestPreview(boundary)

  if (response.ok) return (await response.json()) as TerritoryPerformance

  return thunkApi.rejectWithValue(
    await toTerritoryError(response, READ_FALLBACKS),
  )
})

/** The piece being carved off, plus the details for the territory it becomes. */
export type SplitTerritoryPayload = {
  id: number
  /** The piece being carved off, when the whole shape was traced. */
  boundary?: TerritoryBoundary
  /**
   * A line drawn across the territory, dividing it in two. Either this or `boundary`, never
   * both -- the server refuses both at once rather than guessing which was meant.
   */
  line?: number[][]
  name: string
  colorHex: string
  salesTeamId: number
  ownerIds: string[]
}

/** Both sides come back, because both changed. */
export type SplitTerritoryResult = {
  original: Territory
  created: Territory
}

/**
 * Carve a piece off a territory into a new one (WWM-17508).
 *
 * The boundary is the piece being SPLIT OFF -- the part handed to someone else -- not the
 * dividing line. The original keeps its name, colour and owners for what remains.
 */
export const splitTerritory = createAsyncThunk<
  SplitTerritoryResult,
  SplitTerritoryPayload,
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/split', async ({ id, ...body }, thunkApi) => {
  const response = await post(
    territoryUrl(ServiceConfig.territory.split(id)),
    body,
  )

  if (response.ok) return (await response.json()) as SplitTerritoryResult

  return thunkApi.rejectWithValue(await toTerritoryError(response))
})

/**
 * Combine territories into the target, which survives.
 *
 * Territories that do not share a border are refused by the server: a territory is stored as a
 * single polygon, so two separate areas have nowhere to go.
 */
export const mergeTerritories = createAsyncThunk<
  Territory,
  { targetId: number; sourceIds: number[] },
  { state: StoreData; rejectValue: TerritoryWriteError }
>('territory/merge', async (body, thunkApi) => {
  const response = await post(territoryUrl(ServiceConfig.territory.merge), body)

  if (response.ok) return (await response.json()) as Territory

  return thunkApi.rejectWithValue(await toTerritoryError(response))
})
